import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';

import { environment } from '@environments/environment';
import { ApiResponse } from '@core/types';
import { ChatChannel, ChatChannelCreate, ChatMessage, ChatMessageCreate } from '@core/types/chat.type';

@Injectable({ providedIn: 'root' })
export class ChatService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.api}/chat`;

  // Channels
  getChannels(): Observable<ApiResponse<ChatChannel[]>> {
    return this.http.get<ApiResponse<ChatChannel[]>>(`${this.baseUrl}/channels`).pipe(
      catchError((errorResponse: HttpErrorResponse) =>
        throwError(() => errorResponse.error?.message || 'Error loading chat channels')
      )
    );
  }

  createChannel(data: ChatChannelCreate): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(`${this.baseUrl}/channels`, data).pipe(
      catchError((errorResponse: HttpErrorResponse) =>
        throwError(() => errorResponse.error?.message || 'Error creating chat channel')
      )
    );
  }

  deleteChannel(id: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.baseUrl}/channels/${id}`).pipe(
      catchError((errorResponse: HttpErrorResponse) =>
        throwError(() => errorResponse.error?.message || 'Error deleting chat channel')
      )
    );
  }

  // Messages
  getMessages(channelId: string, params?: { from?: string; to?: string; take?: number }): Observable<ApiResponse<ChatMessage[]>> {
    let httpParams = new HttpParams();
    if (params?.from) httpParams = httpParams.set('from', params.from);
    if (params?.to) httpParams = httpParams.set('to', params.to);
    if (params?.take) httpParams = httpParams.set('take', String(params.take));

    return this.http.get<ApiResponse<ChatMessage[]>>(`${this.baseUrl}/channels/${channelId}/messages`, { params: httpParams }).pipe(
      catchError((errorResponse: HttpErrorResponse) =>
        throwError(() => errorResponse.error?.message || 'Error loading chat messages')
      )
    );
  }

  sendMessage(data: ChatMessageCreate): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(`${this.baseUrl}/messages`, data).pipe(
      catchError((errorResponse: HttpErrorResponse) =>
        throwError(() => errorResponse.error?.message || 'Error sending chat message')
      )
    );
  }

  deleteMessage(id: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.baseUrl}/messages/${id}`).pipe(
      catchError((errorResponse: HttpErrorResponse) =>
        throwError(() => errorResponse.error?.message || 'Error deleting chat message')
      )
    );
  }
}

